import { useState } from 'react';
import { CalendarDays, ChevronDown } from 'lucide-react';
import {
  DATE_RANGE_PRESETS,
  PRESET_LABELS,
  formatDateRange,
  fromInputValue,
  resolveDateRange,
  toInputValue,
  type DateRange,
} from './date-range';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { cn } from '@/lib/utils';

export interface DateRangeOptionsProps {
  label: string;
  /** `null` means the facet is off, which the shared picker has no way to say. */
  value: DateRange | null;
  onChange: (range: DateRange | null) => void;
  /** Prefix for the two date inputs' ids: two lists can share a screen. */
  id: string;
  /**
   * Se eligió y no hay más que hacer acá. Lo usa el desplegable para cerrarse;
   * adentro de un panel no hay nada que cerrar y no se pasa.
   */
  onDone?: () => void;
}

/**
 * Las opciones del período: los presets y, si se pide, el rango a mano.
 *
 * Igual que `FilterOptionList`, vive aparte de su desplegable porque tiene dos
 * casas — el popover de la barra y la sección abierta del panel de filtros.
 */
export function DateRangeOptions({ label, value, onChange, id, onDone }: DateRangeOptionsProps) {
  const [custom, setCustom] = useState(() => ({
    from: toInputValue(value?.from ?? new Date()),
    to: toInputValue(value?.to ?? new Date()),
  }));

  const applyCustom = (next: { from: string; to: string }) => {
    setCustom(next);
    const from = fromInputValue(next.from, 'from');
    const to = fromInputValue(next.to, 'to');
    if (from && to && from <= to) onChange({ from, to, preset: 'personalizado' });
  };

  const invalid =
    value?.preset === 'personalizado' && !(custom.from && custom.to && custom.from <= custom.to);

  return (
    <>
      <div role="radiogroup" aria-label={label}>
        <button
          type="button"
          role="radio"
          aria-checked={value === null}
          onClick={() => {
            onChange(null);
            onDone?.();
          }}
          className={cn(
            'flex w-full items-center rounded-md px-2 py-1.5 text-left text-sm transition-colors duration-150 ease-out hover:bg-muted',
            value === null && 'bg-muted font-medium',
          )}
        >
          Cualquier fecha
        </button>

        {DATE_RANGE_PRESETS.map((preset) => (
          <button
            key={preset}
            type="button"
            role="radio"
            aria-checked={value?.preset === preset}
            onClick={() => {
              if (preset === 'personalizado') {
                const base = value ?? resolveDateRange('30d');
                setCustom({ from: toInputValue(base.from), to: toInputValue(base.to) });
                onChange({ ...base, preset });
                return;
              }
              onChange(resolveDateRange(preset));
              onDone?.();
            }}
            className={cn(
              'flex w-full items-center rounded-md px-2 py-1.5 text-left text-sm transition-colors duration-150 ease-out hover:bg-muted',
              value?.preset === preset && 'bg-muted font-medium',
            )}
          >
            {PRESET_LABELS[preset]}
          </button>
        ))}
      </div>

      {value?.preset === 'personalizado' && (
        <div className="mt-1 space-y-2 border-t border-border p-2">
          <div className="grid grid-cols-2 gap-2">
            <div className="space-y-1">
              <Label htmlFor={`${id}-range-from`} className="text-xs text-muted-foreground">
                Desde
              </Label>
              <Input
                id={`${id}-range-from`}
                type="date"
                value={custom.from}
                max={custom.to}
                onChange={(event) => applyCustom({ ...custom, from: event.target.value })}
                className="h-7"
              />
            </div>
            <div className="space-y-1">
              <Label htmlFor={`${id}-range-to`} className="text-xs text-muted-foreground">
                Hasta
              </Label>
              <Input
                id={`${id}-range-to`}
                type="date"
                value={custom.to}
                min={custom.from}
                onChange={(event) => applyCustom({ ...custom, to: event.target.value })}
                className="h-7"
              />
            </div>
          </div>

          <p role="status" className={cn('text-xs', invalid ? 'text-destructive' : 'sr-only')}>
            {invalid ? 'El inicio debe ser anterior al término.' : 'Rango válido'}
          </p>

          {onDone && (
            <Button size="sm" className="w-full" disabled={invalid} onClick={onDone}>
              Aplicar
            </Button>
          )}
        </div>
      )}
    </>
  );
}

export interface DateFilterProps {
  label: string;
  /** `null` means the facet is off, which the shared picker has no way to say. */
  value: DateRange | null;
  onChange: (range: DateRange | null) => void;
  /** Prefix for the two date inputs' ids: two lists can share a screen. */
  id: string;
}

/** Period facet with an explicit "any date" option, so it can be turned off. */
export function DateFilter({ label, value, onChange, id }: DateFilterProps) {
  const [open, setOpen] = useState(false);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          className={cn('gap-1.5 border-dashed', value && 'border-solid')}
        >
          <CalendarDays className="size-3.5 text-muted-foreground" aria-hidden />
          {label}
          {value && (
            <span className="max-w-40 truncate font-normal text-muted-foreground">
              {formatDateRange(value)}
            </span>
          )}
          <ChevronDown className="size-3.5 text-muted-foreground" aria-hidden />
        </Button>
      </PopoverTrigger>

      <PopoverContent align="start" className="w-64 p-1">
        <DateRangeOptions
          label={label}
          value={value}
          onChange={onChange}
          id={id}
          onDone={() => setOpen(false)}
        />
      </PopoverContent>
    </Popover>
  );
}
